/**
 * 用户素材库 API
 * 管理声音样本等用户素材
 */
import type { ApiEnvelope, ApiResponse } from './types';
import { unwrapApiResponseData } from './types';
import { ApiClient, API_BASE_URL } from './client';
import { getLocalGcpUploadProxyEndpoint, getLocalGcpSignedUploadUrl } from './local-gcp-upload-routing';
import { authFetch } from '@/lib/auth/session';
import { translateClient } from '@/lib/i18n/client';
import { normalizeImageFileSignature } from './image-file-signature';
import { hashUploadFile } from './upload-content-hash';

// ============================================
// 工具函数
// ============================================

export const IMAGE_FILE_INPUT_ACCEPT = 'image/*,.heic,.heif,.avif,.bmp,.tiff,.tif';
const MAX_VIDEO_UPLOAD_MB = 200;
const MAX_VIDEO_UPLOAD_BYTES = MAX_VIDEO_UPLOAD_MB * 1024 * 1024;

/** 从文件扩展名推断 MIME 类型（浏览器对 HEIC/AVIF 等常报空或 application/octet-stream） */
const EXT_MIME_MAP: Record<string, string> = {
  '.jpg': 'image/jpeg', '.jpeg': 'image/jpeg', '.png': 'image/png',
  '.webp': 'image/webp', '.heic': 'image/heic', '.heif': 'image/heif',
  '.gif': 'image/gif', '.bmp': 'image/bmp', '.tiff': 'image/tiff',
  '.tif': 'image/tiff', '.avif': 'image/avif', '.svg': 'image/svg+xml',
  '.mp4': 'video/mp4', '.mov': 'video/quicktime', '.avi': 'video/x-msvideo',
  '.webm': 'video/webm', '.mkv': 'video/x-matroska', '.m4v': 'video/x-m4v',
  '.mpeg': 'video/mpeg', '.mpg': 'video/mpeg', '.3gp': 'video/3gpp',
  '.mp3': 'audio/mpeg', '.wav': 'audio/wav', '.aac': 'audio/aac', '.ogg': 'audio/ogg',
};

export function inferContentType(file: File): string {
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
  return EXT_MIME_MAP[ext] || file.type || 'application/octet-stream';
}

const normalizedUploadFileCache = new WeakMap<File, File>();
const normalizedUploadFileSourceCache = new WeakMap<File, File>();

export function normalizeUploadFile(file: File): File {
  const contentType = inferContentType(file);
  if (!contentType || contentType === file.type) return file;
  const cached = normalizedUploadFileCache.get(file);
  if (cached) return cached;
  const normalized = new File([file], file.name, {
    type: contentType,
    lastModified: file.lastModified,
  });
  normalizedUploadFileCache.set(file, normalized);
  normalizedUploadFileSourceCache.set(normalized, file);
  return normalized;
}

const _IMAGE_OBJECT_LOAD_TIMEOUT_MS = 5000;
const _IMAGE_DIMENSION_HEADER_BYTES = 512 * 1024;
const _UPLOAD_MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MiB
const _UPLOAD_MAX_DIMENSION = 4096;
// Bound the source before asking Canvas to decode pixels. 64 MP still covers
// current high-resolution cameras while preventing tiny compressed files from
// expanding into multi-gigabyte browser allocations.
const _UPLOAD_MAX_SOURCE_PIXELS = 64 * 1024 * 1024;
const _UPLOAD_MAX_SOURCE_DIMENSION = 32 * 1024;
const _SIGNED_UPLOAD_MIN_TIMEOUT_MS = 120_000;
const _SIGNED_UPLOAD_MAX_TIMEOUT_MS = 15 * 60_000;
const _SIGNED_UPLOAD_STALL_TIMEOUT_MS = 45_000;
const _UPLOAD_PROGRESS_THROTTLE_MS = 200;
const _UPLOAD_CONFIRM_TIMEOUT_MS = 30_000;

export type MaterialUploadStage = 'preparing' | 'uploading' | 'retrying' | 'verifying';

export interface MaterialUploadProgress {
  percentage: number;
  bytesUploaded: number;
  bytesTotal: number;
  bytesPerSecond?: number;
  etaSeconds?: number;
  attempt: number;
}

export class SignedUploadError extends Error {
  constructor(
    message: string,
    readonly code: 'upload_timeout' | 'upload_network' | 'upload_http' | 'upload_cancelled',
    readonly status?: number,
  ) {
    super(message);
    this.name = 'SignedUploadError';
  }
}

export interface ImageFileDimensions {
  width: number;
  height: number;
}

export class ImageUploadSafetyError extends Error {
  readonly code = 'image_dimensions_too_large';

  constructor(readonly dimensions: ImageFileDimensions | null) {
    super(dimensions
      ? `Image dimensions are too large to process safely: ${dimensions.width}x${dimensions.height}`
      : 'Image dimensions could not be verified safely before decoding');
    this.name = 'ImageUploadSafetyError';
  }
}

function validImageDimensions(width: number, height: number): ImageFileDimensions | null {
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width <= 0 || height <= 0) {
    return null;
  }
  return { width, height };
}

function readUint24LittleEndian(bytes: Uint8Array, offset: number): number {
  return bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16);
}

function parsePngDimensions(bytes: Uint8Array, view: DataView): ImageFileDimensions | null {
  if (bytes.length < 24
    || bytes[0] !== 0x89 || bytes[1] !== 0x50 || bytes[2] !== 0x4e || bytes[3] !== 0x47
    || bytes[4] !== 0x0d || bytes[5] !== 0x0a || bytes[6] !== 0x1a || bytes[7] !== 0x0a
    || view.getUint32(8, false) !== 13
    || String.fromCharCode(...bytes.subarray(12, 16)) !== 'IHDR') {
    return null;
  }
  return validImageDimensions(view.getUint32(16, false), view.getUint32(20, false));
}

function parseGifDimensions(bytes: Uint8Array, view: DataView): ImageFileDimensions | null {
  if (bytes.length < 10) return null;
  const signature = String.fromCharCode(...bytes.subarray(0, 6));
  if (signature !== 'GIF87a' && signature !== 'GIF89a') return null;
  return validImageDimensions(view.getUint16(6, true), view.getUint16(8, true));
}

function parseBmpDimensions(bytes: Uint8Array, view: DataView): ImageFileDimensions | null {
  if (bytes.length < 26 || bytes[0] !== 0x42 || bytes[1] !== 0x4d) return null;
  const dibHeaderSize = view.getUint32(14, true);
  if (dibHeaderSize === 12) {
    return validImageDimensions(view.getUint16(18, true), view.getUint16(20, true));
  }
  if (dibHeaderSize < 40) return null;
  return validImageDimensions(
    view.getInt32(18, true),
    Math.abs(view.getInt32(22, true)),
  );
}

function parseTiffDimensions(bytes: Uint8Array, view: DataView): ImageFileDimensions | null {
  if (bytes.length < 8) return null;
  const byteOrder = String.fromCharCode(bytes[0], bytes[1]);
  if (byteOrder !== 'II' && byteOrder !== 'MM') return null;
  const littleEndian = byteOrder === 'II';
  if (view.getUint16(2, littleEndian) !== 42) return null;
  const ifdOffset = view.getUint32(4, littleEndian);
  if (ifdOffset + 2 > bytes.length) return null;
  const entryCount = view.getUint16(ifdOffset, littleEndian);
  let width: number | null = null;
  let height: number | null = null;
  for (let index = 0; index < entryCount; index += 1) {
    const entryOffset = ifdOffset + 2 + (index * 12);
    if (entryOffset + 12 > bytes.length) return null;
    const tag = view.getUint16(entryOffset, littleEndian);
    if (tag !== 256 && tag !== 257) continue;
    const type = view.getUint16(entryOffset + 2, littleEndian);
    const count = view.getUint32(entryOffset + 4, littleEndian);
    if (count !== 1 || (type !== 3 && type !== 4)) continue;
    const value = type === 3
      ? view.getUint16(entryOffset + 8, littleEndian)
      : view.getUint32(entryOffset + 8, littleEndian);
    if (tag === 256) width = value;
    else height = value;
    if (width !== null && height !== null) return validImageDimensions(width, height);
  }
  return null;
}

interface IsoBox {
  type: string;
  start: number;
  payloadStart: number;
  end: number;
}

function readIsoBox(bytes: Uint8Array, view: DataView, offset: number, limit: number): IsoBox | null {
  if (offset < 0 || offset + 8 > limit) return null;
  const size32 = view.getUint32(offset, false);
  const type = String.fromCharCode(...bytes.subarray(offset + 4, offset + 8));
  let headerSize = 8;
  let size: number;
  if (size32 === 1) {
    if (offset + 16 > limit) return null;
    const size64 = view.getBigUint64(offset + 8, false);
    if (size64 > BigInt(Number.MAX_SAFE_INTEGER)) return null;
    size = Number(size64);
    headerSize = 16;
  } else if (size32 === 0) {
    size = limit - offset;
  } else {
    size = size32;
  }
  if (size < headerSize || size > limit - offset) return null;
  return {
    type,
    start: offset,
    payloadStart: offset + headerSize,
    end: offset + size,
  };
}

function findIsoChildBoxes(
  bytes: Uint8Array,
  view: DataView,
  start: number,
  end: number,
): IsoBox[] | null {
  const boxes: IsoBox[] = [];
  let offset = start;
  while (offset < end) {
    const box = readIsoBox(bytes, view, offset, end);
    if (!box || box.end <= offset) return null;
    boxes.push(box);
    offset = box.end;
  }
  return offset === end ? boxes : null;
}

function parseIsoFtypBrands(bytes: Uint8Array): Set<string> | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const box = readIsoBox(bytes, view, 0, bytes.length);
  if (!box || box.type !== 'ftyp' || box.payloadStart + 8 > box.end
    || (box.end - (box.payloadStart + 8)) % 4 !== 0) {
    return null;
  }
  const brands = new Set<string>();
  brands.add(String.fromCharCode(...bytes.subarray(box.payloadStart, box.payloadStart + 4)));
  for (let offset = box.payloadStart + 8; offset + 4 <= box.end; offset += 4) {
    brands.add(String.fromCharCode(...bytes.subarray(offset, offset + 4)));
  }
  return brands;
}

function isoBrandsMatchContentType(brands: Set<string>, contentType: string): boolean {
  if (contentType === 'image/avif') {
    return brands.has('avif') || brands.has('avis');
  }
  if (contentType === 'image/heic') {
    return ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis'].some((brand) => brands.has(brand));
  }
  return ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1']
    .some((brand) => brands.has(brand));
}

function readPrimaryItemId(bytes: Uint8Array, view: DataView, pitm: IsoBox): number | null {
  if (pitm.payloadStart + 6 > pitm.end) return null;
  const version = bytes[pitm.payloadStart];
  if (version === 0) return view.getUint16(pitm.payloadStart + 4, false);
  if (version === 1 && pitm.payloadStart + 8 <= pitm.end) {
    return view.getUint32(pitm.payloadStart + 4, false);
  }
  return null;
}

function parseIsoPropertyDimensions(
  bytes: Uint8Array,
  view: DataView,
  ipco: IsoBox,
): Map<number, ImageFileDimensions> | null {
  const properties = findIsoChildBoxes(bytes, view, ipco.payloadStart, ipco.end);
  if (!properties) return null;
  const dimensionsByProperty = new Map<number, ImageFileDimensions>();
  properties.forEach((property, index) => {
    if (property.type !== 'ispe' || property.payloadStart + 12 > property.end) return;
    const dimensions = validImageDimensions(
      view.getUint32(property.payloadStart + 4, false),
      view.getUint32(property.payloadStart + 8, false),
    );
    if (dimensions) dimensionsByProperty.set(index + 1, dimensions);
  });
  return dimensionsByProperty;
}

function parseAssociatedPrimaryDimensions(
  bytes: Uint8Array,
  view: DataView,
  ipma: IsoBox,
  primaryItemId: number,
  dimensionsByProperty: Map<number, ImageFileDimensions>,
): ImageFileDimensions | null {
  if (ipma.payloadStart + 8 > ipma.end) return null;
  const version = bytes[ipma.payloadStart];
  if (version > 1) return null;
  const flags = (bytes[ipma.payloadStart + 1] << 16)
    | (bytes[ipma.payloadStart + 2] << 8)
    | bytes[ipma.payloadStart + 3];
  const wideAssociations = (flags & 1) !== 0;
  const itemIdBytes = version === 0 ? 2 : 4;
  const associationBytes = wideAssociations ? 2 : 1;
  const entryCount = view.getUint32(ipma.payloadStart + 4, false);
  let offset = ipma.payloadStart + 8;
  for (let entry = 0; entry < entryCount; entry += 1) {
    if (offset + itemIdBytes + 1 > ipma.end) return null;
    const itemId = itemIdBytes === 2
      ? view.getUint16(offset, false)
      : view.getUint32(offset, false);
    offset += itemIdBytes;
    const associationCount = bytes[offset];
    offset += 1;
    let maxWidth = 0;
    let maxHeight = 0;
    for (let association = 0; association < associationCount; association += 1) {
      if (offset + associationBytes > ipma.end) return null;
      const rawAssociation = associationBytes === 2
        ? view.getUint16(offset, false)
        : bytes[offset];
      offset += associationBytes;
      const propertyIndex = rawAssociation & (wideAssociations ? 0x7fff : 0x7f);
      if (itemId !== primaryItemId || propertyIndex === 0) continue;
      const dimensions = dimensionsByProperty.get(propertyIndex);
      if (!dimensions) continue;
      maxWidth = Math.max(maxWidth, dimensions.width);
      maxHeight = Math.max(maxHeight, dimensions.height);
    }
    if (itemId === primaryItemId && maxWidth > 0 && maxHeight > 0) {
      return validImageDimensions(maxWidth, maxHeight);
    }
  }
  return null;
}

function parseIsoMetaDimensions(bytes: Uint8Array): ImageFileDimensions | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const meta = readIsoBox(bytes, view, 0, bytes.length);
  if (!meta || meta.type !== 'meta' || meta.payloadStart + 4 > meta.end) return null;
  const metaChildren = findIsoChildBoxes(bytes, view, meta.payloadStart + 4, meta.end);
  if (!metaChildren) return null;
  const pitm = metaChildren.find((box) => box.type === 'pitm');
  const iprp = metaChildren.find((box) => box.type === 'iprp');
  if (!pitm || !iprp) return null;
  const primaryItemId = readPrimaryItemId(bytes, view, pitm);
  if (primaryItemId === null) return null;

  const propertyChildren = findIsoChildBoxes(bytes, view, iprp.payloadStart, iprp.end);
  if (!propertyChildren) return null;
  const ipco = propertyChildren.find((box) => box.type === 'ipco');
  const ipmaBoxes = propertyChildren.filter((box) => box.type === 'ipma');
  if (!ipco || ipmaBoxes.length === 0) return null;
  const dimensionsByProperty = parseIsoPropertyDimensions(bytes, view, ipco);
  if (!dimensionsByProperty || dimensionsByProperty.size === 0) return null;
  for (const ipma of ipmaBoxes) {
    const dimensions = parseAssociatedPrimaryDimensions(
      bytes,
      view,
      ipma,
      primaryItemId,
      dimensionsByProperty,
    );
    if (dimensions) return dimensions;
  }
  return null;
}

function createAbortError(): Error {
  if (typeof DOMException !== 'undefined') {
    return new DOMException('The operation was aborted', 'AbortError');
  }
  const error = new Error('The operation was aborted');
  error.name = 'AbortError';
  return error;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw createAbortError();
}

function waitForPromiseWithSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  throwIfAborted(signal);
  return new Promise<T>((resolve, reject) => {
    const handleAbort = () => reject(createAbortError());
    signal.addEventListener('abort', handleAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      },
    );
  });
}

async function readFileBytes(file: File, start: number, end: number): Promise<Uint8Array> {
  if (start < 0 || end < start || end > file.size) return new Uint8Array();
  return new Uint8Array(await file.slice(start, end).arrayBuffer());
}

async function readJpegDimensionsFromFile(file: File): Promise<ImageFileDimensions | null> {
  const scanLimit = Math.min(file.size, _UPLOAD_MAX_IMAGE_BYTES);
  const signature = await readFileBytes(file, 0, Math.min(2, scanLimit));
  if (signature.length !== 2 || signature[0] !== 0xff || signature[1] !== 0xd8) return null;
  const startOfFrameMarkers = new Set([
    0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7,
    0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
  ]);
  let offset = 2;
  while (offset < scanLimit) {
    const markerPrefix = await readFileBytes(file, offset, Math.min(offset + 32, scanLimit));
    if (markerPrefix.length < 2 || markerPrefix[0] !== 0xff) return null;
    let markerOffset = 1;
    while (markerOffset < markerPrefix.length && markerPrefix[markerOffset] === 0xff) markerOffset += 1;
    if (markerOffset >= markerPrefix.length) {
      offset += markerOffset;
      continue;
    }
    const marker = markerPrefix[markerOffset];
    offset += markerOffset + 1;
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
    if (marker === 0xd9 || marker === 0xda || offset + 2 > scanLimit) return null;
    const lengthBytes = await readFileBytes(file, offset, offset + 2);
    if (lengthBytes.length !== 2) return null;
    const segmentLength = (lengthBytes[0] << 8) | lengthBytes[1];
    if (segmentLength < 2 || segmentLength > scanLimit - offset) return null;
    if (startOfFrameMarkers.has(marker)) {
      if (segmentLength < 7) return null;
      const frameHeader = await readFileBytes(file, offset, offset + 7);
      if (frameHeader.length !== 7) return null;
      return validImageDimensions(
        (frameHeader[5] << 8) | frameHeader[6],
        (frameHeader[3] << 8) | frameHeader[4],
      );
    }
    offset += segmentLength;
  }
  return null;
}

async function readWebpDimensionsFromFile(file: File): Promise<ImageFileDimensions | null> {
  const header = await readFileBytes(file, 0, Math.min(12, file.size));
  if (header.length !== 12
    || String.fromCharCode(...header.subarray(0, 4)) !== 'RIFF'
    || String.fromCharCode(...header.subarray(8, 12)) !== 'WEBP') return null;
  const headerView = new DataView(header.buffer, header.byteOffset, header.byteLength);
  const riffEnd = headerView.getUint32(4, true) + 8;
  if (riffEnd > file.size || riffEnd < 20) return null;
  let offset = 12;
  while (offset + 8 <= riffEnd && offset < _IMAGE_DIMENSION_HEADER_BYTES) {
    const chunkHeader = await readFileBytes(file, offset, offset + 8);
    if (chunkHeader.length !== 8) return null;
    const chunkView = new DataView(chunkHeader.buffer, chunkHeader.byteOffset, chunkHeader.byteLength);
    const chunkLength = chunkView.getUint32(4, true);
    const dataOffset = offset + 8;
    if (chunkLength > riffEnd - dataOffset) return null;
    const bytesNeeded = Math.min(chunkLength, 10);
    const chunkBytes = await readFileBytes(file, dataOffset, dataOffset + bytesNeeded);
    const chunkType = String.fromCharCode(...chunkHeader.subarray(0, 4));
    if (chunkType === 'VP8X' && chunkLength >= 10 && chunkBytes.length >= 10) {
      return validImageDimensions(
        readUint24LittleEndian(chunkBytes, 4) + 1,
        readUint24LittleEndian(chunkBytes, 7) + 1,
      );
    }
    if (chunkType === 'VP8L' && chunkLength >= 5 && chunkBytes.length >= 5 && chunkBytes[0] === 0x2f) {
      return validImageDimensions(
        1 + chunkBytes[1] + ((chunkBytes[2] & 0x3f) << 8),
        1 + ((chunkBytes[2] & 0xc0) >> 6) + (chunkBytes[3] << 2) + ((chunkBytes[4] & 0x0f) << 10),
      );
    }
    if (chunkType === 'VP8 ' && chunkLength >= 10 && chunkBytes.length >= 10
      && chunkBytes[3] === 0x9d && chunkBytes[4] === 0x01 && chunkBytes[5] === 0x2a) {
      const dataView = new DataView(chunkBytes.buffer, chunkBytes.byteOffset, chunkBytes.byteLength);
      return validImageDimensions(
        dataView.getUint16(6, true) & 0x3fff,
        dataView.getUint16(8, true) & 0x3fff,
      );
    }
    offset = dataOffset + chunkLength + (chunkLength % 2);
  }
  return null;
}

async function readTiffDimensionsFromFile(file: File): Promise<ImageFileDimensions | null> {
  const header = await readFileBytes(file, 0, Math.min(8, file.size));
  if (header.length !== 8) return null;
  const byteOrder = String.fromCharCode(header[0], header[1]);
  if (byteOrder !== 'II' && byteOrder !== 'MM') return null;
  const littleEndian = byteOrder === 'II';
  const headerView = new DataView(header.buffer, header.byteOffset, header.byteLength);
  if (headerView.getUint16(2, littleEndian) !== 42) return null;
  const ifdOffset = headerView.getUint32(4, littleEndian);
  if (ifdOffset > _IMAGE_DIMENSION_HEADER_BYTES || ifdOffset + 2 > file.size) return null;
  const countBytes = await readFileBytes(file, ifdOffset, ifdOffset + 2);
  if (countBytes.length !== 2) return null;
  const entryCount = new DataView(countBytes.buffer, countBytes.byteOffset, 2).getUint16(0, littleEndian);
  const directoryEnd = ifdOffset + 2 + (entryCount * 12);
  if (directoryEnd > file.size || directoryEnd > _IMAGE_DIMENSION_HEADER_BYTES) return null;
  const prefix = await readFileBytes(file, 0, directoryEnd);
  return parseTiffDimensions(prefix, new DataView(prefix.buffer, prefix.byteOffset, prefix.byteLength));
}

async function readIcoDimensionsFromFile(file: File): Promise<ImageFileDimensions | null> {
  const header = await readFileBytes(file, 0, Math.min(6, file.size));
  if (header.length !== 6) return null;
  const headerView = new DataView(header.buffer, header.byteOffset, header.byteLength);
  if (headerView.getUint16(0, true) !== 0 || headerView.getUint16(2, true) !== 1) return null;
  const count = headerView.getUint16(4, true);
  const directoryEnd = 6 + (count * 16);
  if (count < 1 || directoryEnd > file.size || directoryEnd > _IMAGE_DIMENSION_HEADER_BYTES) return null;
  // The directory offsets must be checked against the real file size. A tiny
  // placeholder extends the inspection buffer without copying image payloads.
  const directory = await readFileBytes(file, 0, directoryEnd);
  let maxWidth = 0;
  let maxHeight = 0;
  const view = new DataView(directory.buffer, directory.byteOffset, directory.byteLength);
  for (let index = 0; index < count; index += 1) {
    const entryOffset = 6 + (index * 16);
    const imageBytes = view.getUint32(entryOffset + 8, true);
    const imageOffset = view.getUint32(entryOffset + 12, true);
    if (imageBytes === 0 || imageOffset < directoryEnd
      || imageOffset > file.size || imageBytes > file.size - imageOffset) return null;
    maxWidth = Math.max(maxWidth, directory[entryOffset] || 256);
    maxHeight = Math.max(maxHeight, directory[entryOffset + 1] || 256);
  }
  return validImageDimensions(maxWidth, maxHeight);
}

async function readIsoBoxBytesFromFile(file: File, offset: number): Promise<{ box: IsoBox; bytes: Uint8Array } | null> {
  const header = await readFileBytes(file, offset, Math.min(file.size, offset + 16));
  if (header.length < 8) return null;
  const view = new DataView(header.buffer, header.byteOffset, header.byteLength);
  const size32 = view.getUint32(0, false);
  let size: number;
  let headerSize = 8;
  if (size32 === 1) {
    if (header.length < 16) return null;
    const size64 = view.getBigUint64(8, false);
    if (size64 > BigInt(Number.MAX_SAFE_INTEGER)) return null;
    size = Number(size64);
    headerSize = 16;
  } else if (size32 === 0) {
    size = file.size - offset;
  } else {
    size = size32;
  }
  if (size < headerSize || size > file.size - offset) return null;
  const type = String.fromCharCode(...header.subarray(4, 8));
  return {
    box: { type, start: offset, payloadStart: offset + headerSize, end: offset + size },
    bytes: header,
  };
}

async function readIsoBaseMediaDimensionsFromFile(
  file: File,
  contentType: string,
): Promise<ImageFileDimensions | null> {
  let offset = 0;
  let brands: Set<string> | null = null;
  let dimensions: ImageFileDimensions | null = null;
  for (let boxCount = 0; offset < file.size && boxCount < 1024; boxCount += 1) {
    const root = await readIsoBoxBytesFromFile(file, offset);
    if (!root || root.box.end <= offset) return null;
    const boxSize = root.box.end - root.box.start;
    if (root.box.type === 'ftyp') {
      if (boxSize > 64 * 1024) return null;
      const boxBytes = await readFileBytes(file, root.box.start, root.box.end);
      brands = parseIsoFtypBrands(boxBytes);
      if (!brands || !isoBrandsMatchContentType(brands, contentType)) return null;
    } else if (root.box.type === 'meta') {
      if (boxSize > _IMAGE_DIMENSION_HEADER_BYTES) return null;
      const boxBytes = await readFileBytes(file, root.box.start, root.box.end);
      dimensions = parseIsoMetaDimensions(boxBytes);
      if (!dimensions) return null;
    }
    if (brands && dimensions) return dimensions;
    offset = root.box.end;
  }
  return null;
}

const imageDimensionsPromiseCache = new WeakMap<File, Promise<ImageFileDimensions | null>>();

async function inspectImageFileDimensions(file: File): Promise<ImageFileDimensions | null> {
  const contentType = inferContentType(file).toLowerCase();
  if (!contentType.startsWith('image/') || file.size === 0) return null;
  try {
    if (contentType === 'image/jpeg' || contentType === 'image/jpg') {
      return readJpegDimensionsFromFile(file);
    }
    if (contentType === 'image/webp') return readWebpDimensionsFromFile(file);
    if (contentType === 'image/tiff' || contentType === 'image/tif') {
      return readTiffDimensionsFromFile(file);
    }
    if (contentType === 'image/x-icon' || contentType === 'image/vnd.microsoft.icon') {
      return readIcoDimensionsFromFile(file);
    }
    if (contentType === 'image/avif' || contentType === 'image/heic' || contentType === 'image/heif') {
      return readIsoBaseMediaDimensionsFromFile(file, contentType);
    }
    const requiredBytes = contentType === 'image/png' ? 24
      : contentType === 'image/gif' ? 10
        : contentType === 'image/bmp' ? 26
          : Math.min(file.size, 32);
    const bytes = await readFileBytes(file, 0, Math.min(file.size, requiredBytes));
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return parsePngDimensions(bytes, view)
      || parseGifDimensions(bytes, view)
      || parseBmpDimensions(bytes, view);
  } catch {
    return null;
  }
}

/**
 * Intrinsic dimensions from format-specific bounded reads. The result promise
 * is cached by File identity so preview, preparation and confirmation do not
 * repeatedly copy the same bytes. Unknown or unverifiable containers return
 * null and known raster callers fail closed before invoking a decoder.
 */
export function readImageFileDimensions(
  file: File,
  signal?: AbortSignal,
): Promise<ImageFileDimensions | null> {
  const cacheKey = normalizedUploadFileSourceCache.get(file) || file;
  let dimensionsPromise = imageDimensionsPromiseCache.get(cacheKey);
  if (!dimensionsPromise) {
    dimensionsPromise = inspectImageFileDimensions(file);
    imageDimensionsPromiseCache.set(cacheKey, dimensionsPromise);
  }
  return waitForPromiseWithSignal(dimensionsPromise, signal);
}

interface LoadedImageElement {
  image: HTMLImageElement;
  dimensions: ImageFileDimensions;
  dispose: () => void;
}

async function loadImageElementFromFile(
  file: File,
  timeoutMs: number = _IMAGE_OBJECT_LOAD_TIMEOUT_MS,
  signal?: AbortSignal,
): Promise<LoadedImageElement | null> {
  const normalizedFile = normalizeUploadFile(file);
  throwIfAborted(signal);

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(normalizedFile);
    const img = new Image();
    let settled = false;
    let objectUrlRevoked = false;

    const revokeObjectUrl = () => {
      if (objectUrlRevoked) return;
      objectUrlRevoked = true;
      URL.revokeObjectURL(url);
    };
    const releaseElement = () => {
      img.onload = null;
      img.onerror = null;
      img.removeAttribute('src');
    };

    const finish = (loaded: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', handleAbort);
      img.onload = null;
      img.onerror = null;
      revokeObjectUrl();
      const dimensions = loaded
        ? validImageDimensions(img.naturalWidth || img.width, img.naturalHeight || img.height)
        : null;
      if (!dimensions) {
        releaseElement();
        resolve(null);
        return;
      }
      let disposed = false;
      resolve({
        image: img,
        dimensions,
        dispose: () => {
          if (disposed) return;
          disposed = true;
          revokeObjectUrl();
          releaseElement();
        },
      });
    };
    const handleAbort = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', handleAbort);
      revokeObjectUrl();
      releaseElement();
      reject(createAbortError());
    };

    const timer = window.setTimeout(() => {
      console.warn('[MaterialUpload] 图片本地解析超时，跳过前端尺寸提取/转码:', normalizedFile.name);
      finish(false);
    }, timeoutMs);

    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    signal?.addEventListener('abort', handleAbort, { once: true });
    if (signal?.aborted) {
      handleAbort();
      return;
    }
    img.src = url;
  });
}

function getSignedUploadTimeoutMs(fileSize: number): number {
  // 以 128 KiB/s 的保守弱网上行估算，同时保留总时长硬上限。
  const weakNetworkEstimate = Math.ceil(fileSize / (128 * 1024) * 1000) + 60_000;
  return Math.min(_SIGNED_UPLOAD_MAX_TIMEOUT_MS, Math.max(_SIGNED_UPLOAD_MIN_TIMEOUT_MS, weakNetworkEstimate));
}

export async function uploadFileToSignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
  attempt: number,
  onProgress?: (progress: MaterialUploadProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const startedAt = performance.now();
    let lastProgressAt = 0;
    let stallTimer: number | undefined;
    let settled = false;

    const clearStallTimer = () => {
      if (stallTimer !== undefined) {
        window.clearTimeout(stallTimer);
        stallTimer = undefined;
      }
    };
    const cleanup = () => {
      clearStallTimer();
      signal?.removeEventListener('abort', handleExternalAbort);
    };
    const resolveOnce = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };
    const rejectOnce = (error: SignedUploadError) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const handleExternalAbort = () => {
      xhr.abort();
      rejectOnce(new SignedUploadError('File upload cancelled', 'upload_cancelled'));
    };
    const armStallTimer = () => {
      clearStallTimer();
      stallTimer = window.setTimeout(() => {
        xhr.abort();
        rejectOnce(new SignedUploadError('File upload stalled', 'upload_timeout'));
      }, _SIGNED_UPLOAD_STALL_TIMEOUT_MS);
    };

    xhr.upload.onprogress = (event) => {
      // Activity without a computable total still proves the transfer is live.
      armStallTimer();
      if (!event.lengthComputable) return;
      const now = performance.now();
      const isTerminalProgress = event.loaded >= event.total;
      if (!isTerminalProgress && now - lastProgressAt < _UPLOAD_PROGRESS_THROTTLE_MS) return;
      lastProgressAt = now;
      if (isTerminalProgress) clearStallTimer();
      const elapsedSeconds = Math.max((now - startedAt) / 1000, 0.001);
      const bytesPerSecond = event.loaded / elapsedSeconds;
      const remainingBytes = Math.max(0, event.total - event.loaded);
      onProgress?.({
        percentage: Math.min(100, Math.round((event.loaded / event.total) * 100)),
        bytesUploaded: event.loaded,
        bytesTotal: event.total,
        bytesPerSecond,
        etaSeconds: bytesPerSecond > 0 ? Math.ceil(remainingBytes / bytesPerSecond) : undefined,
        attempt,
      });
    };
    xhr.upload.onload = clearStallTimer;
    xhr.onload = () => {
      clearStallTimer();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolveOnce();
        return;
      }
      rejectOnce(new SignedUploadError(`File upload failed (${xhr.status})`, 'upload_http', xhr.status));
    };
    xhr.onerror = () => {
      clearStallTimer();
      rejectOnce(new SignedUploadError('File upload failed', 'upload_network'));
    };
    xhr.ontimeout = () => {
      clearStallTimer();
      rejectOnce(new SignedUploadError('File upload timed out', 'upload_timeout'));
    };
    xhr.onabort = () => {
      clearStallTimer();
      if (signal?.aborted) {
        rejectOnce(new SignedUploadError('File upload cancelled', 'upload_cancelled'));
      }
    };

    if (signal?.aborted) {
      rejectOnce(new SignedUploadError('File upload cancelled', 'upload_cancelled'));
      return;
    }
    signal?.addEventListener('abort', handleExternalAbort, { once: true });

    xhr.open('PUT', getLocalGcpSignedUploadUrl(uploadUrl));
    xhr.timeout = getSignedUploadTimeoutMs(file.size);
    xhr.setRequestHeader('Content-Type', contentType);
    armStallTimer();
    xhr.send(file);
  });
}

  /**
   * 检测 JPEG 文件是否为 MPO (Multi-Picture Object) 格式。
   * MPO 文件的扩展名和 MIME 都是 image/jpeg，但包含多帧图像。
   * AI 供应商（如腾讯 VOD）会拒绝这类文件："Invalid image file or mode for image 1"。
   */
  export async function detectMpoImage(file: File, signal?: AbortSignal): Promise<boolean> {
    throwIfAborted(signal);
    const ct = inferContentType(file);
    if (ct !== 'image/jpeg' && ct !== 'image/jpg') return false;

    try {
      // Read header bytes to scan for APP2 "MPF\0" marker
      const header = await file.slice(0, Math.min(file.size, 131072)).arrayBuffer();
      throwIfAborted(signal);
      const bytes = new Uint8Array(header);

      let i = 2; // skip JPEG SOI (0xFF 0xD8)
      while (i < bytes.length - 8) {
        if (bytes[i] !== 0xFF) break;
        const marker = bytes[i + 1];
        if (marker === 0xDA) break; // SOS – no more APP markers after this

        if (marker === 0xE2) {
          // APP2: check for "MPF\0" signature
          const segLen = (bytes[i + 2] << 8) | bytes[i + 3];
          if (segLen >= 8) {
            const sig = String.fromCharCode(bytes[i + 4], bytes[i + 5], bytes[i + 6], bytes[i + 7]);
            if (sig === 'MPF\0') return true;
          }
          i += 2 + segLen;
        } else if (marker >= 0xE0 && marker <= 0xEF) {
          // Other APPn markers – skip
          const segLen = (bytes[i + 2] << 8) | bytes[i + 3];
          i += 2 + segLen;
        } else if ((marker >= 0xC0 && marker <= 0xCF) || marker === 0xDB || marker === 0xC4 || marker === 0xDD) {
          // SOF / DHT / DQT – markers with segment length
          const segLen = (bytes[i + 2] << 8) | bytes[i + 3];
          i += 2 + segLen;
        } else {
          i += 2;
        }
      }
      return false;
    } catch (error) {
      if (signal?.aborted || (error instanceof Error && error.name === 'AbortError')) throw error;
      return false;
    }
  }

function imageDimensionsExceedUploadLimit(dimensions: ImageFileDimensions): boolean {
  return Math.max(dimensions.width, dimensions.height) > _UPLOAD_MAX_DIMENSION;
}

function requiresVerifiedRasterDimensions(contentType: string): boolean {
  return new Set([
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
    'image/bmp', 'image/tiff', 'image/tif',
    'image/x-icon', 'image/vnd.microsoft.icon',
    'image/avif', 'image/heic', 'image/heif',
  ]).has(contentType);
}

function assertImageDimensionsSafeToDecode(dimensions: ImageFileDimensions): void {
  const exceedsDimension = Math.max(dimensions.width, dimensions.height) > _UPLOAD_MAX_SOURCE_DIMENSION;
  const exceedsPixelBudget = dimensions.width > (_UPLOAD_MAX_SOURCE_PIXELS / dimensions.height);
  if (exceedsDimension || exceedsPixelBudget) {
    throw new ImageUploadSafetyError(dimensions);
  }
}

/**
 * Validate a local image before any caller creates a preview object URL. Known
 * raster containers must expose safe dimensions; oversized unknown containers
 * fail closed rather than delegating an unbounded decode to the browser.
 */
export async function assertImageFileSafeForPreview(
  file: File,
  signal?: AbortSignal,
): Promise<ImageFileDimensions | null> {
  throwIfAborted(signal);
  const normalizedFile = normalizeUploadFile(file);
  const contentType = inferContentType(normalizedFile).toLowerCase();
  if (!contentType.startsWith('image/') || contentType === 'image/svg+xml') return null;
  const dimensions = await readImageFileDimensions(file, signal);
  throwIfAborted(signal);
  if (dimensions) {
    assertImageDimensionsSafeToDecode(dimensions);
    return dimensions;
  }
  if (requiresVerifiedRasterDimensions(contentType)
    || normalizedFile.size > _UPLOAD_MAX_IMAGE_BYTES) {
    throw new ImageUploadSafetyError(null);
  }
  return null;
}

function replaceImageExtensionWithJpeg(name: string): string {
  const dotIndex = name.lastIndexOf('.');
  return `${dotIndex > 0 ? name.slice(0, dotIndex) : name}.jpg`;
}

function canvasToJpegBlob(
  canvas: HTMLCanvasElement,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

async function encodeLoadedImageAsUploadJpeg(
  sourceFile: File,
  loaded: LoadedImageElement,
  forceStandardJpeg: boolean,
  signal?: AbortSignal,
): Promise<File> {
  throwIfAborted(signal);
  const { image, dimensions } = loaded;
  const scale = Math.min(1, _UPLOAD_MAX_DIMENSION / Math.max(dimensions.width, dimensions.height));
  let targetWidth = Math.max(1, Math.round(dimensions.width * scale));
  let targetHeight = Math.max(1, Math.round(dimensions.height * scale));
  const canvas = document.createElement('canvas');

  try {
    const renderAtCurrentSize = (): boolean => {
      throwIfAborted(signal);
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const context = canvas.getContext('2d');
      if (!context) return false;
      context.drawImage(image, 0, 0, targetWidth, targetHeight);
      return true;
    };

    if (!renderAtCurrentSize()) {
      throw new Error('Canvas 2D context is unavailable for image preparation');
    }

    const qualities = forceStandardJpeg
      ? [0.92, 0.85, 0.75, 0.65]
      : [0.9, 0.8, 0.7, 0.6];
    let outputBlob: Blob | null = null;
    for (const quality of qualities) {
      throwIfAborted(signal);
      outputBlob = await canvasToJpegBlob(canvas, quality);
      throwIfAborted(signal);
      if (outputBlob && outputBlob.size <= _UPLOAD_MAX_IMAGE_BYTES) break;
    }

    // Exceptionally noisy images may still exceed 10 MiB at 4096px. Keep
    // reducing dimensions with the lowest quality instead of returning the
    // original over-limit file.
    for (let reduction = 0;
      (!outputBlob || outputBlob.size > _UPLOAD_MAX_IMAGE_BYTES) && reduction < 4;
      reduction += 1) {
      targetWidth = Math.max(1, Math.round(targetWidth / 2));
      targetHeight = Math.max(1, Math.round(targetHeight / 2));
      if (!renderAtCurrentSize()) {
        throw new Error('Canvas 2D context is unavailable for image preparation');
      }
      outputBlob = await canvasToJpegBlob(canvas, 0.6);
      throwIfAborted(signal);
    }

    if (!outputBlob || outputBlob.size > _UPLOAD_MAX_IMAGE_BYTES) {
      throw new Error('Image could not be compressed within the upload safety limit');
    }

    console.info(
      forceStandardJpeg ? '[MPO Normalize] MPO/多帧图已重编码为标准 JPEG:' : '[ImageCompress] 完成:',
      `${(sourceFile.size / 1024).toFixed(0)}KB → ${(outputBlob.size / 1024).toFixed(0)}KB`,
      `(${targetWidth}x${targetHeight})`,
    );
    return new File([outputBlob], replaceImageExtensionWithJpeg(sourceFile.name), {
      type: 'image/jpeg',
      lastModified: sourceFile.lastModified,
    });
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

async function prepareRasterImageForUpload(
  file: File,
  options: { forceStandardJpeg?: boolean } = {},
  signal?: AbortSignal,
): Promise<File> {
  throwIfAborted(signal);
  const normalizedFile = normalizeUploadFile(file);
  const contentType = inferContentType(normalizedFile).toLowerCase();
  if (!contentType.startsWith('image/')) return normalizedFile;

  // Canvas would flatten vector semantics. SVG retains its original bytes and
  // is validated by the upload/backend contract.
  if (!options.forceStandardJpeg && contentType === 'image/svg+xml') {
    return normalizedFile;
  }

  const headerDimensions = await readImageFileDimensions(normalizedFile, signal);
  throwIfAborted(signal);
  if (headerDimensions) assertImageDimensionsSafeToDecode(headerDimensions);
  const exceedsByteLimit = normalizedFile.size > _UPLOAD_MAX_IMAGE_BYTES;
  // Animated GIF must not pass through Canvas, but its logical screen size is
  // still cheap to validate before preview or upload.
  if (!options.forceStandardJpeg && contentType === 'image/gif') {
    if (!headerDimensions) throw new ImageUploadSafetyError(null);
    return normalizedFile;
  }
  if (!headerDimensions && requiresVerifiedRasterDimensions(contentType)) {
    throw new ImageUploadSafetyError(null);
  }
  // HEIC left here could not be decoded by heic2any. Native Chrome Image
  // decoding cannot compress it either; the confirm endpoint owns conversion
  // and output limits. Keep the verified source bytes for that endpoint.
  if (!options.forceStandardJpeg && (contentType === 'image/heic' || contentType === 'image/heif')) {
    return normalizedFile;
  }
  const headerExceedsDimensionLimit = headerDimensions
    ? imageDimensionsExceedUploadLimit(headerDimensions)
    : false;

  // Header dimensions are authoritative for supported containers, so normal
  // small images avoid Image decoding and object-URL allocation entirely.
  if (!options.forceStandardJpeg
    && headerDimensions
    && !exceedsByteLimit
    && !headerExceedsDimensionLimit) {
    return normalizedFile;
  }

  throwIfAborted(signal);
  const loaded = await loadImageElementFromFile(normalizedFile, _IMAGE_OBJECT_LOAD_TIMEOUT_MS, signal);
  throwIfAborted(signal);
  if (!loaded) {
    if (exceedsByteLimit || headerExceedsDimensionLimit) {
      throw new Error('Oversized image could not be decoded for safe compression');
    }
    return normalizedFile;
  }

  try {
    assertImageDimensionsSafeToDecode(loaded.dimensions);
    const exceedsDimensionLimit = imageDimensionsExceedUploadLimit(loaded.dimensions);
    if (!options.forceStandardJpeg && !exceedsByteLimit && !exceedsDimensionLimit) {
      return normalizedFile;
    }
    return await encodeLoadedImageAsUploadJpeg(
      normalizedFile,
      loaded,
      options.forceStandardJpeg === true,
      signal,
    );
  } finally {
    loaded.dispose();
  }
}

/**
 * 通过 Canvas 将图片重编码为标准单帧 JPEG。
 * 用于消除 MPO 等多帧格式，确保向 AI 供应商提交标准图片。
 */
export async function reencodeImageToStandardJpeg(file: File, signal?: AbortSignal): Promise<File> {
  return prepareRasterImageForUpload(file, { forceStandardJpeg: true }, signal);
}

export function isImageFile(file: File): boolean {
  return inferContentType(file).startsWith('image/');
}

export function isVideoFile(file: File): boolean {
  return inferContentType(file).startsWith('video/');
}

/** Prepare pixels for browser-only tools; HEIF decoding may require the server codec. */
export async function prepareBrowserImageFile(file: File, signal?: AbortSignal): Promise<File> {
  const prepared = await prepareImageUploadFile(file, signal);
  throwIfAborted(signal);
  if (!['image/heic', 'image/heif'].includes(inferContentType(prepared))) return prepared;
  const body = new FormData();
  body.append('file', prepared);
  const path = '/upload/image/prepare';
  const response = await authFetch(getLocalGcpUploadProxyEndpoint(path, 'POST') ?? `${API_BASE_URL}${path}`, {
    method: 'POST', body, signal,
  });
  if (!response.ok || response.headers.get('content-type')?.split(';')[0].trim() !== 'image/jpeg') {
    throw new Error('IMAGE_PREPARE_FAILED');
  }
  const blob = await response.blob();
  throwIfAborted(signal);
  if (!blob.size || blob.size > _UPLOAD_MAX_IMAGE_BYTES) throw new Error('IMAGE_PREPARE_FAILED');
  return new File([blob], `${prepared.name.replace(/\.[^.]+$/, '') || 'image'}.jpg`, {
    type: 'image/jpeg', lastModified: prepared.lastModified,
  });
}

/** HEIC/HEIF → JPEG 转换（Chrome 不支持 HEIC 显示，必须转换） */
interface CachedFilePreparation { result: Promise<File>; controller: AbortController; consumers: number; settled: boolean }
const heicConversionCache = new WeakMap<File, CachedFilePreparation>();
const imagePreparationCache = new WeakMap<File, CachedFilePreparation>();
let activeImagePreparations = 0;
const imagePreparationWaiters: Array<() => void> = [];

async function withImagePreparationBudget<T>(run: () => Promise<T>): Promise<T> {
  if (activeImagePreparations >= 2) await new Promise<void>((resolve) => imagePreparationWaiters.push(resolve));
  else activeImagePreparations += 1;
  try { return await run(); }
  finally {
    const next = imagePreparationWaiters.shift();
    if (next) next();
    else activeImagePreparations -= 1;
  }
}

function cachedFilePreparation(
  cache: WeakMap<File, CachedFilePreparation>, file: File, run: (signal: AbortSignal) => Promise<File>, signal?: AbortSignal,
): Promise<File> {
  throwIfAborted(signal);
  const key = normalizedUploadFileSourceCache.get(file) || file;
  let entry = cache.get(key);
  if (!entry) {
    const controller = new AbortController();
    entry = { controller, consumers: 0, settled: false, result: Promise.resolve().then(() => run(controller.signal)) };
    cache.set(key, entry);
    const current = entry;
    void entry.result.then(() => { current.settled = true; }, () => {
      current.settled = true;
      if (cache.get(key) === current) cache.delete(key);
    });
  }
  const current = entry;
  current.consumers += 1;
  return waitForPromiseWithSignal(current.result, signal).finally(() => {
    current.consumers -= 1;
    if (!current.settled && current.consumers === 0) {
      if (cache.get(key) === current) cache.delete(key);
      current.controller.abort();
    }
  });
}

export function convertHeicToJpeg(file: File, signal?: AbortSignal): Promise<File> {
  return cachedFilePreparation(heicConversionCache, file,
    (sharedSignal) => withImagePreparationBudget(() => convertHeicToJpegUncached(file, sharedSignal)), signal);
}

async function convertHeicToJpegUncached(file: File, signal?: AbortSignal): Promise<File> {
  throwIfAborted(signal);
  const normalizedFile = normalizeUploadFile(file);
  const ct = inferContentType(normalizedFile);
  if (ct !== 'image/heic' && ct !== 'image/heif') return normalizedFile;

  try {
    // heic2any decodes before the common compression path sees a JPEG. Guard
    // the source container here so conversion cannot bypass the pixel budget.
    const dimensions = await readImageFileDimensions(normalizedFile, signal);
    throwIfAborted(signal);
    if (dimensions) {
      assertImageDimensionsSafeToDecode(dimensions);
    } else {
      throw new ImageUploadSafetyError(null);
    }
    throwIfAborted(signal);
    const heic2any = (await import('heic2any')).default;
    throwIfAborted(signal);
    const blob = await heic2any({ blob: normalizedFile, toType: 'image/jpeg', quality: 0.92 });
    throwIfAborted(signal);
    const jpegBlob = Array.isArray(blob) ? blob[0] : blob;
    const newName = normalizedFile.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg');
    return new File([jpegBlob], newName, {
      type: 'image/jpeg',
      lastModified: normalizedFile.lastModified,
    });
  } catch (e) {
    if (e instanceof ImageUploadSafetyError || signal?.aborted || (e instanceof Error && e.name === 'AbortError')) throw e;
    console.info('[HEIC Convert] 前端转换失败，将由后端转换:', e);
    return normalizedFile;
  }
}

export function createLocalImagePreviewUrl(file: File): string | null {
  const normalizedFile = normalizeUploadFile(file);
  const contentType = inferContentType(normalizedFile).toLowerCase();
  if (!contentType.startsWith('image/')) {
    return null;
  }
  if (contentType === 'image/heic' || contentType === 'image/heif') {
    return null;
  }
  return URL.createObjectURL(normalizedFile);
}

export function prepareImageUploadFile(file: File, signal?: AbortSignal): Promise<File> {
  return cachedFilePreparation(imagePreparationCache, file, (sharedSignal) => prepareImageUploadFileUncached(file, sharedSignal), signal);
}

async function prepareImageUploadFileUncached(file: File, signal?: AbortSignal): Promise<File> {
  throwIfAborted(signal);
  // 1. Make the declared MIME/extension match the actual safe raster bytes.
  let processed = await normalizeImageFileSignature(file);
  throwIfAborted(signal);

  // 2. HEIC/HEIF → JPEG
  processed = await convertHeicToJpeg(processed, signal);
  throwIfAborted(signal);

  // 3. MPO / 多帧 JPEG → 标准单帧 JPEG（防止伪装成 .jpg 的多帧容器被 AI 供应商拒绝）
  if (await detectMpoImage(processed, signal)) {
    throwIfAborted(signal);
    // Re-encoding also enforces byte/dimension limits with the same decoded
    // element, so do not decode the newly-created JPEG a second time.
    return withImagePreparationBudget(() => reencodeImageToStandardJpeg(processed, signal));
  }

  // 4. 大图压缩
  throwIfAborted(signal);
  return withImagePreparationBudget(() => compressImageForUpload(processed, signal));
}

/**
 * 上传前压缩图片（AI API 限制 10 MiB）
 * 仅在栅格图片超过 10 MiB 或任一边超过 4096px 时处理。
 * GIF/SVG 保持原语义，不通过 Canvas 栅格化。
 */
export async function compressImageForUpload(file: File, signal?: AbortSignal): Promise<File> {
  return prepareRasterImageForUpload(file, {}, signal);
}

// ============================================
// 类型定义
// ============================================

/** 素材分类 */
export type AssetCategory = 'ai_generated' | 'user_material' | 'project_asset';

/** 用户素材类型 */
export type MaterialType = 'voice_sample' | 'general';

/** 系统默认素材集合 */
export type DefaultMaterialCollectionSlug = 'product' | 'studio' | 'character' | 'style' | 'image';

export interface SystemStylePreset {
  style_id: string;
  preset_id: string;
  display_name_en: string;
  display_name_zh: string;
  cover_url: string;
  preview_input_url: string;
  preview_output_url: string;
  display_order: number;
}

/** 用户素材项 */
export interface UserMaterial {
  id: string;
  name: string;
  display_name?: string;
  original_filename?: string;
  file_type: 'video' | 'audio' | 'image' | 'subtitle' | 'prompt';
  mime_type?: string;
  file_size?: number;
  storage_path: string;
  url?: string;
  thumbnail_url?: string;
  duration?: number;
  width?: number;
  height?: number;
  asset_category: AssetCategory;
  material_type: MaterialType;
  category?: string;
  material_metadata?: Record<string, unknown>;
  tags?: string[];
  is_favorite?: boolean;
  usage_count?: number;
  last_used_at?: string;
  created_at: string;
  updated_at?: string;
  deleted_at?: string | null;
  expires_at?: string | null;
}

/** 声音样本 */
export interface VoiceSampleItem {
  id: string;
  type: 'sample' | 'clone';
  name: string;
  url?: string;
  duration?: number;
  is_cloned?: boolean;
  language?: string;
  gender?: string;
  preview_url?: string;
  usage_count?: number;
  metadata?: Record<string, unknown>;
  created_at: string;
}

/** 素材上传请求 */
interface MaterialUploadRequest {
  file_name: string;
  content_type: string;
  file_size: number;
  material_type: MaterialType;
  display_name?: string;
  tags?: string[];
  material_metadata?: Record<string, unknown>;
  content_sha256?: string;
  asset_category?: AssetCategory;
  category?: string;
}

/** 素材上传响应 */
interface MaterialUploadResponse {
  asset_id: string;
  upload_url: string;
  storage_path: string;
  /** presign 阶段发现服务端已验证相同内容时为 true */
  duplicate?: boolean;
  /** dedup 命中时后端返回的可访问 URL */
  url?: string;
  thumbnail_url?: string;
  /** dedup 命中时返回的文件类型 */
  file_type?: string;
  /** dedup 命中时返回的原始尺寸 */
  width?: number;
  height?: number;
  name?: string;
  display_name?: string;
  duration?: number;
  created_at?: string;
  updated_at?: string;
  material?: UserMaterial;
}

/** Persist before PUT so a recovered save can confirm the same owned object. */
export interface MaterialUploadCheckpoint {
  asset_id: string;
  storage_path: string;
  material_type: MaterialType;
  file_name: string;
  content_type: string;
  file_size: number;
  display_name?: string;
  tags?: string;
  duration?: number;
  width?: number;
  height?: number;
  asset_category?: AssetCategory;
  category?: string;
  content_sha256?: string;
}

export interface MaterialUploadOptions {
  displayName?: string;
  tags?: string[];
  onProgress?: (progress: number, details?: MaterialUploadProgress) => void;
  onStageChange?: (stage: MaterialUploadStage) => void;
  assetCategory?: AssetCategory;
  category?: string;
  defaultCollectionSlug?: DefaultMaterialCollectionSlug;
  signal?: AbortSignal;
  onCheckpoint?: (checkpoint: MaterialUploadCheckpoint, preparedFile: File) => Promise<void>;
}

/** 用户素材偏好设置 */
interface MaterialPreferences {
  default_voice_type: 'preset' | 'cloned';
  default_voice_id?: string;
  default_broadcast_settings: {
    aspect_ratio: string;
    model: string;
    lip_sync_mode: string;
  };
}

/** 设置默认素材请求 */
interface SetDefaultMaterialRequest {
  material_type: 'voice';
  asset_id?: string;
  voice_type?: 'preset' | 'cloned';
}

/** 素材集合 */
export interface AssetCollection {
  id: string;
  user_id: string;
  name: string;
  name_en?: string;
  name_zh?: string;
  description?: string;
  description_en?: string;
  description_zh?: string;
  system_slug?: DefaultMaterialCollectionSlug | null;
  display_order?: number;
  cover_images?: string[];
  asset_count?: number;
  created_at: string;
  updated_at: string;
}

interface ImportFromUrlOptions {
  publicImageId?: string;
  fileType?: 'image' | 'video' | 'audio';
  collectionId?: string;
  defaultCollectionSlug?: DefaultMaterialCollectionSlug;
}

// ============================================
// API Client
// ============================================

export class MaterialsApi extends ApiClient {
  private parseDownloadFilename(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return utf8Match[1];
      }
    }

    const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return plainMatch?.[1]?.trim() || null;
  }

  private async downloadArchive(
    endpoint: string,
    payload: Record<string, unknown>,
    fallbackFilename: string,
  ): Promise<ApiResponse<{ filename: string }>> {
    try {
      const response = await authFetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-ID': crypto.randomUUID().replace(/-/g, '').slice(0, 12),
        },
        body: JSON.stringify(payload),
        timeout: 600000,
        retries: 1,
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let rawMessage = '';

        if (contentType.includes('application/json')) {
          const errorData = await response.json().catch(() => null) as { detail?: unknown } | null;
          if (typeof errorData?.detail === 'string' && errorData.detail.trim()) {
            rawMessage = errorData.detail;
          }
        } else {
          const errorText = await response.text().catch(() => '');
          if (errorText.trim()) {
            rawMessage = errorText;
          }
        }

        return {
          error: {
            code: `HTTP_${response.status}`,
            message: translateClient('workflow', 'downloadFailedSafe'),
            details: rawMessage ? { rawMessage } : undefined,
          },
        };
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        return {
          error: {
            code: 'EMPTY_DOWNLOAD',
            message: translateClient('workflow', 'downloadFailedSafe'),
          },
        };
      }

      const filename = this.parseDownloadFilename(response.headers.get('content-disposition')) || fallbackFilename;
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);

      return { data: { filename } };
    } catch (error) {
      return {
        error: {
          code: 'DOWNLOAD_FAILED',
          message: translateClient('workflow', 'downloadFailedSafe'),
          details: error instanceof Error ? { rawMessage: error.message } : undefined,
        },
      };
    }
  }

  private async addAssetToDefaultCollection(
    assetId: string,
    defaultCollectionSlug: DefaultMaterialCollectionSlug,
  ): Promise<ApiResponse<{ success: boolean }>> {
    const collectionsResponse = await this.getCollections();
    if (collectionsResponse.error || !collectionsResponse.data) {
      return {
        error: collectionsResponse.error || {
          code: 'DEFAULT_COLLECTION_LOAD_FAILED',
          message: translateClient('workflow', 'materialCollectionsLoadFailed'),
        },
      };
    }

    const targetCollection = collectionsResponse.data.find(
      (collection) => collection.system_slug === defaultCollectionSlug,
    );
    if (!targetCollection) {
      return {
        error: {
          code: 'DEFAULT_COLLECTION_NOT_FOUND',
          message: `Default material collection not found: ${defaultCollectionSlug}`,
        },
      };
    }

    return this.addToCollection(targetCollection.id, [assetId]);
  }

  /**
   * 获取用户素材列表
   */
  async getMaterials(params?: {
    material_type?: MaterialType;
    file_type?: string;
    asset_category?: 'user_material' | 'ai_generated' | 'all';
    usage_scope?: 'kagemane';
    category?: string;
    collection_id?: string;
    is_favorite?: boolean;
    tags?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }, options?: { signal?: AbortSignal }): Promise<ApiResponse<{ items: UserMaterial[]; total: number }>> {
    const searchParams = new URLSearchParams();
    if (params?.material_type) searchParams.set('material_type', params.material_type);
    if (params?.file_type) searchParams.set('file_type', params.file_type);
    if (params?.asset_category) searchParams.set('asset_category', params.asset_category);
    if (params?.usage_scope) searchParams.set('usage_scope', params.usage_scope);
    if (params?.category) searchParams.set('category', params.category);
    if (params?.collection_id) searchParams.set('collection_id', params.collection_id);
    if (params?.is_favorite !== undefined) searchParams.set('is_favorite', String(params.is_favorite));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.tags) searchParams.set('tags', params.tags);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const query = searchParams.toString();
    return this.request(`/materials${query ? `?${query}` : ''}`, options);
  }

  async rememberKagemaneMaterials(assetIds: string[]): Promise<ApiResponse<{ asset_ids: string[] }>> {
    return this.request('/materials/kagemane-usage', {
      method: 'POST', body: JSON.stringify({ asset_ids: assetIds }),
    });
  }

  async getSystemStylePresets(): Promise<ApiResponse<SystemStylePreset[]>> {
    const response = await this.request<ApiEnvelope<SystemStylePreset[]> | SystemStylePreset[]>(
      '/materials/system-style-presets',
    );
    return unwrapApiResponseData<SystemStylePreset[]>(response, {
      code: 'INVALID_RESPONSE',
      message: translateClient('errors', 'common.fallbackMessage'),
    });
  }

  async selectSystemPreset(assetId: string): Promise<ApiResponse<UserMaterial>> {
    const response = await this.request<ApiEnvelope<UserMaterial>>(
      `/materials/presets/${encodeURIComponent(assetId)}/select`, { method: 'POST' },
    );
    return unwrapApiResponseData<UserMaterial>(response, {
      code: 'INVALID_RESPONSE', message: translateClient('errors', 'common.fallbackMessage'),
    });
  }

  /**
   * 获取声音样本列表（包含已克隆的声音）
   */
  async getVoiceSamples(params?: {
    include_clones?: boolean;
    limit?: number;
  }): Promise<ApiResponse<{ items: VoiceSampleItem[] }>> {
    const searchParams = new URLSearchParams();
    if (params?.include_clones !== undefined) searchParams.set('include_clones', String(params.include_clones));
    if (params?.limit) searchParams.set('limit', String(params.limit));

    const query = searchParams.toString();
    return this.request(`/materials/voice-samples${query ? `?${query}` : ''}`);
  }

  /**
   * 获取预签名上传 URL
   */
  async presignUpload(data: MaterialUploadRequest, signal?: AbortSignal): Promise<ApiResponse<MaterialUploadResponse>> {
    return this.request('/materials/presign-upload', {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    });
  }

  /**
   * 确认素材上传完成
   */
  async confirmUpload(data: MaterialUploadCheckpoint, signal?: AbortSignal): Promise<ApiResponse<UserMaterial>> {
    const params = new URLSearchParams({
      material_type: data.material_type,
      file_name: data.file_name,
      content_type: data.content_type,
      file_size: String(data.file_size),
    });
    if (data.display_name) params.set('display_name', data.display_name);
    if (data.tags) params.set('tags', data.tags);
    if (data.asset_category) params.set('asset_category', data.asset_category);
    if (data.category) params.set('category', data.category);

    return this.request(`/materials/confirm-upload?${params.toString()}`, {
      method: 'POST',
      body: JSON.stringify({
        asset_id: data.asset_id,
        storage_path: data.storage_path,
        duration: data.duration,
        width: data.width,
        height: data.height,
        content_sha256: data.content_sha256,
      }),
      signal,
    });
  }

  /**
   * 更新素材信息
   */
  async updateMaterial(assetId: string, data: {
    display_name?: string;
    tags?: string[];
    is_favorite?: boolean;
    material_metadata?: Record<string, unknown>;
    category?: string;
  }): Promise<ApiResponse<UserMaterial>> {
    return this.request(`/materials/${assetId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * 删除素材
   */
  async deleteMaterial(assetId: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return this.request(`/materials/${assetId}`, {
      method: 'DELETE',
    });
  }

  async batchDeleteMaterials(assetIds: string[]): Promise<ApiResponse<{ success: boolean; deleted: number; deleted_ids?: string[]; requested: number }>> {
    return this.request('/materials/batch-delete', {
      method: 'POST',
      body: JSON.stringify({ asset_ids: assetIds }),
    });
  }

  async getTrashItems(params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{ items: UserMaterial[]; total: number; limit: number; offset: number }>> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const query = searchParams.toString();
    return this.request(`/materials/trash${query ? `?${query}` : ''}`);
  }

  async restoreFromTrash(assetIds: string[]): Promise<ApiResponse<{ restored: number; restored_ids: string[] }>> {
    return this.request('/materials/trash/restore', {
      method: 'POST',
      body: JSON.stringify({ asset_ids: assetIds }),
    });
  }

  async permanentlyDelete(assetIds: string[]): Promise<ApiResponse<{ deleted: number; deleted_ids: string[] }>> {
    return this.request('/materials/trash/permanent-delete', {
      method: 'POST',
      body: JSON.stringify({ asset_ids: assetIds }),
    });
  }

  /**
   * 记录素材使用
   */
  async recordUsage(assetId: string): Promise<ApiResponse<{ success: boolean; usage_count: number }>> {
    return this.request(`/materials/${assetId}/use`, {
      method: 'POST',
    });
  }

  /**
   * 将素材提升到用户素材库
   * 把 project_asset / ai_generated 转为 user_material
   */
  async promoteToLibrary(assetId: string): Promise<ApiResponse<{ success: boolean; message: string; duplicate?: boolean }>> {
    return this.request(`/materials/${assetId}/promote-to-library`, {
      method: 'POST',
    });
  }

  /**
   * 获取用户素材偏好设置
   */
  async getPreferences(): Promise<ApiResponse<MaterialPreferences>> {
    return this.request('/materials/preferences');
  }

  /**
   * 设置默认素材
   */
  async setDefaultMaterial(data: SetDefaultMaterialRequest): Promise<ApiResponse<{ success: boolean }>> {
    return this.request('/materials/preferences/default', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * 获取去重的 category 列表（自动补全用）
   */
  async getCategories(materialType?: MaterialType): Promise<ApiResponse<{ categories: string[] }>> {
    const qs = materialType ? `?material_type=${materialType}` : '';
    return this.request(`/materials/categories${qs}`);
  }

  /**
   * 从 URL 导入素材到素材库
   */
  /**
   * 保存 prompt 文本为素材
   */
  async savePrompt(data: {
    prompt_text: string;
    display_name?: string;
    tags?: string[];
    collection_id?: string;
  }): Promise<ApiResponse<UserMaterial>> {
    const response = await this.request<{ success: boolean; data: UserMaterial } | UserMaterial>('/materials/save-prompt', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return unwrapApiResponseData<UserMaterial>(response as ApiResponse<ApiEnvelope<UserMaterial> | UserMaterial>, {
      code: 'INVALID_RESPONSE',
      message: translateClient('workflow', 'saveFailed'),
    });
  }

  async importFromUrl(
    sourceUrl: string,
    assetCategory: AssetCategory,
    category?: string,
    options?: ImportFromUrlOptions,
  ): Promise<ApiResponse<{ success: boolean; asset: UserMaterial; duplicate?: boolean }>> {
    const body: Record<string, string> = { source_url: sourceUrl, asset_category: assetCategory };
    if (category) body.category = category;
    if (options?.publicImageId) body.public_image_id = options.publicImageId;
    if (options?.fileType) body.file_type = options.fileType;
    if (options?.collectionId) body.collection_id = options.collectionId;
    if (options?.defaultCollectionSlug) body.default_collection_slug = options.defaultCollectionSlug;
    return this.request('/materials/import-from-url', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * 一站式上传素材
   * 包含：presign -> upload -> confirm 完整流程
   * presign 阶段只复用服务端已校验内容的同作用域素材。
   */
  async uploadMaterial(
    file: File,
    materialType: MaterialType,
    options?: MaterialUploadOptions
  ): Promise<ApiResponse<UserMaterial> & { skipped?: boolean }> {
    try {
      if (options?.signal?.aborted) {
        return { error: { code: 'upload_cancelled', message: translateClient('workflow', 'uploadCancelled') } };
      }
      options?.onStageChange?.('preparing');
      options?.onProgress?.(0);
      const preparedFile = isImageFile(file)
        ? await prepareImageUploadFile(file, options?.signal)
        : normalizeUploadFile(file);
      if (options?.signal?.aborted) {
        return { error: { code: 'upload_cancelled', message: translateClient('workflow', 'uploadCancelled') } };
      }
      const contentType = inferContentType(preparedFile);
      if (contentType.startsWith('video/') && preparedFile.size > MAX_VIDEO_UPLOAD_BYTES) {
        return {
          error: {
            code: 'video_too_large',
            message: translateClient('workflow', 'videoUploadTooLarge', {
              size: (preparedFile.size / 1024 / 1024).toFixed(1),
              limit: MAX_VIDEO_UPLOAD_MB,
            }),
          },
        };
      }
      const contentSha256 = await hashUploadFile(preparedFile, options?.signal);

      // 1. 获取预签名URL（含去重检查）
      const presignResult = await this.presignUpload({
        file_name: preparedFile.name,
        content_type: contentType,
        file_size: preparedFile.size,
        material_type: materialType,
        display_name: options?.displayName,
        tags: options?.tags,
        content_sha256: contentSha256,
        asset_category: options?.assetCategory,
        category: options?.category,
      }, options?.signal);

      if (options?.signal?.aborted) {
        return { error: { code: 'upload_cancelled', message: translateClient('workflow', 'uploadCancelled') } };
      }

      if (presignResult.error || !presignResult.data) {
        return { error: presignResult.error || { code: 'presign_failed', message: translateClient('workflow', 'uploadFailedSafe') } };
      }

      // 去重命中：文件已存在，跳过上传，但需要获取 URL
      if (presignResult.data.duplicate) {
        if (options?.defaultCollectionSlug) {
          const collectionResponse = await this.addAssetToDefaultCollection(
            presignResult.data.asset_id,
            options.defaultCollectionSlug,
          );
          if (collectionResponse.error) {
            return { error: collectionResponse.error };
          }
        }
        options?.onProgress?.(100);
        return {
          success: true,
          data: presignResult.data.material ?? {
            id: presignResult.data.asset_id,
            url: presignResult.data.url || '',
            thumbnail_url: presignResult.data.thumbnail_url || (presignResult.data.file_type === 'image' ? presignResult.data.url : undefined),
            name: presignResult.data.name || preparedFile.name,
            display_name: presignResult.data.display_name || options?.displayName,
            duration: presignResult.data.duration,
            file_type: presignResult.data.file_type,
            width: presignResult.data.width,
            height: presignResult.data.height,
            created_at: presignResult.data.created_at,
            updated_at: presignResult.data.updated_at,
          } as UserMaterial,
          skipped: true,
        };
      }

      const { asset_id, upload_url, storage_path } = presignResult.data;

      // Inspect local metadata before PUT; confirmation must depend on stored bytes.
      let duration: number | undefined;
      let width: number | undefined;
      let height: number | undefined;

      if (contentType.startsWith('audio/') || contentType.startsWith('video/')) {
        const mediaInfo = await this.extractMediaInfo(preparedFile);
        duration = mediaInfo.duration;
        width = mediaInfo.width;
        height = mediaInfo.height;
      } else if (contentType.startsWith('image/')) {
        const imageInfo = await this.extractImageInfo(preparedFile, options?.signal);
        width = imageInfo.width;
        height = imageInfo.height;
      }

      await options?.onCheckpoint?.({
        asset_id, storage_path, material_type: materialType,
        file_name: preparedFile.name, content_type: contentType, file_size: preparedFile.size,
        display_name: options?.displayName, tags: options?.tags?.join(','),
        duration, width, height, asset_category: options?.assetCategory,
        category: options?.category, content_sha256: contentSha256,
      }, preparedFile);
      if (options?.signal?.aborted) {
        return { error: { code: 'upload_cancelled', message: translateClient('workflow', 'uploadCancelled') } };
      }
      options?.onStageChange?.('uploading');
      try {
        await uploadFileToSignedUrl(
          upload_url, preparedFile, contentType, 1,
          (details) => {
            const overallProgress = Math.min(95, 5 + Math.round(details.percentage * 0.9));
            options?.onProgress?.(overallProgress, details);
          }, options?.signal,
        );
      } catch (error) {
        const signedError = error instanceof SignedUploadError ? error : null;
        if (signedError?.code === 'upload_cancelled' || options?.signal?.aborted) {
          return { error: { code: 'upload_cancelled', message: translateClient('workflow', 'uploadCancelled') } };
        }
        if (signedError?.code !== 'upload_timeout' && signedError?.code !== 'upload_network') {
          return { error: { code: signedError?.code || 'upload_failed', message: translateClient('workflow', 'uploadFailedSafe') } };
        }
        // A lost PUT receipt does not mean a lost object. The owner-fenced
        // confirm endpoint checks storage before persisting; never repeat PUT
        // merely because the browser could not observe its acknowledgement.
      }
      options?.onStageChange?.('verifying');
      options?.onProgress?.(96);
      if (options?.signal?.aborted) {
        return { error: { code: 'upload_cancelled', message: translateClient('workflow', 'uploadCancelled') } };
      }

      // 4. 确认上传
      const confirmController = new AbortController();
      let confirmTimedOut = false;
      const forwardAbort = () => confirmController.abort();
      options?.signal?.addEventListener('abort', forwardAbort, { once: true });
      const confirmTimeout = window.setTimeout(() => {
        confirmTimedOut = true;
        confirmController.abort();
      }, _UPLOAD_CONFIRM_TIMEOUT_MS);
      let confirmResult: ApiResponse<UserMaterial>;
      try {
        confirmResult = await this.confirmUpload({
          asset_id,
          storage_path,
          material_type: materialType,
          file_name: preparedFile.name,
          content_type: contentType,
          file_size: preparedFile.size,
          display_name: options?.displayName,
          tags: options?.tags?.join(','),
          duration,
          width,
          height,
          asset_category: options?.assetCategory,
          category: options?.category,
          content_sha256: contentSha256,
        }, confirmController.signal);
      } finally {
        window.clearTimeout(confirmTimeout);
        options?.signal?.removeEventListener('abort', forwardAbort);
      }

      if (options?.signal?.aborted) {
        return { error: { code: 'upload_cancelled', message: translateClient('workflow', 'uploadCancelled') } };
      }
      if (confirmTimedOut) {
        return { error: { code: 'confirm_timeout', message: translateClient('workflow', 'uploadConfirmTimedOut') } };
      }

      if (confirmResult.error) {
        return confirmResult;
      }

      if (options?.defaultCollectionSlug && confirmResult.data?.id) {
        const collectionResponse = await this.addAssetToDefaultCollection(
          confirmResult.data.id,
          options.defaultCollectionSlug,
        );
        if (collectionResponse.error) {
          return { error: collectionResponse.error };
        }
      }

      options?.onProgress?.(100);

      return confirmResult;
    } catch (error) {
      if (options?.signal?.aborted) {
        return { error: { code: 'upload_cancelled', message: translateClient('workflow', 'uploadCancelled') } };
      }
      if (error instanceof ImageUploadSafetyError) {
        return {
          error: {
            code: error.code,
            message: translateClient('workflow', 'imageUploadDimensionsTooLarge'),
          },
        };
      }
      if (typeof error === 'object' && error !== null && 'code' in error && error.code === 'upload_recovery_unavailable') {
        return { error: { code: 'upload_recovery_unavailable', message: translateClient('workflow', 'uploadRecoveryUnavailable') } };
      }
      return { error: { code: 'upload_error', message: translateClient('workflow', 'uploadFailedSafe') } };
    }
  }

  /** Confirm the original PUT before deciding whether another upload is needed. */
  async resumeMaterialUpload(
    file: File, materialType: MaterialType, checkpoint?: MaterialUploadCheckpoint, options?: MaterialUploadOptions,
  ): Promise<ApiResponse<UserMaterial> & { skipped?: boolean }> {
    if (options?.signal?.aborted) {
      return { error: { code: 'upload_cancelled', message: translateClient('workflow', 'uploadCancelled') } };
    }
    if (checkpoint) {
      options?.onStageChange?.('verifying');
      try {
        const result = await this.confirmUpload(checkpoint, options?.signal);
        if (options?.signal?.aborted) {
          return { error: { code: 'upload_cancelled', message: translateClient('workflow', 'uploadCancelled') } };
        }
        if (!result.error && result.data) {
          if (options?.defaultCollectionSlug) {
            const assigned = await this.addAssetToDefaultCollection(result.data.id, options.defaultCollectionSlug);
            if (assigned.error) return { error: assigned.error };
          }
          options?.onProgress?.(100);
          return result;
        }
        if (result.error?.code !== 'material_upload_object_missing') return result;
      } catch {
        return { error: { code: options?.signal?.aborted ? 'upload_cancelled' : 'confirm_timeout',
          message: translateClient('workflow', options?.signal?.aborted ? 'uploadCancelled' : 'uploadConfirmTimedOut') } };
      }
    }
    return this.uploadMaterial(file, materialType, options);
  }

  // ============================================
  // 素材集合（Collections）
  // ============================================

  async getCollections(options?: { includeCovers?: boolean }): Promise<ApiResponse<AssetCollection[]>> {
    const res = await this.request<{ success: boolean; data: AssetCollection[] } | AssetCollection[]>(options?.includeCovers === false ? '/materials/collections?include_covers=false' : '/materials/collections');
    return unwrapApiResponseData<AssetCollection[]>(res as ApiResponse<ApiEnvelope<AssetCollection[]> | AssetCollection[]>, {
      code: 'INVALID_RESPONSE',
      message: translateClient('errors', 'common.fallbackMessage'),
    });
  }

  async createCollection(name: string, description?: string): Promise<ApiResponse<AssetCollection>> {
    const res = await this.request<{ success: boolean; data: AssetCollection } | AssetCollection>('/materials/collections', {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
    return unwrapApiResponseData<AssetCollection>(res as ApiResponse<ApiEnvelope<AssetCollection> | AssetCollection>, {
      code: 'INVALID_RESPONSE',
      message: translateClient('errors', 'common.fallbackMessage'),
    });
  }

  async updateCollection(id: string, data: { name?: string; description?: string }): Promise<ApiResponse<AssetCollection>> {
    const res = await this.request<{ success: boolean; data: AssetCollection } | AssetCollection>(`/materials/collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return unwrapApiResponseData<AssetCollection>(res as ApiResponse<ApiEnvelope<AssetCollection> | AssetCollection>, {
      code: 'INVALID_RESPONSE',
      message: translateClient('errors', 'common.fallbackMessage'),
    });
  }

  async deleteCollection(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/materials/collections/${id}`, { method: 'DELETE' });
  }

  async batchDeleteCollections(ids: string[]): Promise<ApiResponse<{ success: boolean; deleted: number }>> {
    return this.request('/materials/collections/batch-delete', {
      method: 'POST',
      body: JSON.stringify({ collection_ids: ids }),
    });
  }

  async addToCollection(collectionId: string, assetIds: string[]): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/materials/collections/${collectionId}/add`, {
      method: 'POST',
      body: JSON.stringify({ asset_ids: assetIds }),
    });
  }

  async removeFromCollection(collectionId: string, assetIds: string[]): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/materials/collections/${collectionId}/remove`, {
      method: 'POST',
      body: JSON.stringify({ asset_ids: assetIds }),
    });
  }

  async downloadMaterialsArchive(assetIds: string[]): Promise<ApiResponse<{ filename: string }>> {
    return this.downloadArchive('/materials/batch-download', { asset_ids: assetIds }, 'materials.zip');
  }

  async downloadCollectionsArchive(collectionIds: string[]): Promise<ApiResponse<{ filename: string }>> {
    return this.downloadArchive('/materials/collections/batch-download', { collection_ids: collectionIds }, 'collections.zip');
  }

  /**
   * 提取媒体文件信息
   */
  private async extractMediaInfo(file: File): Promise<{ duration?: number; width?: number; height?: number }> {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const isVideo = inferContentType(file).startsWith('video/');
      const media = isVideo
        ? document.createElement('video')
        : document.createElement('audio');

      // ★ 必须设置 preload + muted，否则部分浏览器不会主动加载 metadata，promise 永远不 resolve
      media.preload = 'metadata';
      if (isVideo) (media as HTMLVideoElement).muted = true;

      // ★ 超时兜底（5s），避免 codec 不支持或浏览器拒绝加载时永久挂起
      const timer = setTimeout(() => {
        media.src = '';
        URL.revokeObjectURL(url);
        resolve({});
      }, 5000);

      media.onloadedmetadata = () => {
        clearTimeout(timer);
        const info: { duration?: number; width?: number; height?: number } = {
          duration: media.duration && isFinite(media.duration) ? media.duration : undefined,
        };
        if (media instanceof HTMLVideoElement) {
          info.width = media.videoWidth;
          info.height = media.videoHeight;
        }
        URL.revokeObjectURL(url);
        resolve(info);
      };

      media.onerror = () => {
        clearTimeout(timer);
        URL.revokeObjectURL(url);
        resolve({});
      };

      media.src = url;
    });
  }

  /**
   * 提取图片信息
   */
  private async extractImageInfo(
    file: File,
    signal?: AbortSignal,
  ): Promise<{ width?: number; height?: number }> {
    throwIfAborted(signal);
    const headerDimensions = await readImageFileDimensions(file, signal);
    throwIfAborted(signal);
    if (headerDimensions) {
      return headerDimensions;
    }

    const contentType = inferContentType(file).toLowerCase();
    if (requiresVerifiedRasterDimensions(contentType)) {
      throw new ImageUploadSafetyError(null);
    }
    const loaded = await loadImageElementFromFile(file, _IMAGE_OBJECT_LOAD_TIMEOUT_MS, signal);
    throwIfAborted(signal);
    if (!loaded) {
      return {};
    }

    try {
      return loaded.dimensions;
    } finally {
      loaded.dispose();
    }
  }
}

// 导出单例
export const materialsApi = new MaterialsApi();
